import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction, useRueckfrage } from "../../components/ui";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";

/**
 * KI-Zugänge (0204, Inhaber 09.10.2026): Schlüssel für Claude, ChatGPT und andere auf die Serverfunktion „ki-api“.
 * Rechte je Schlüssel; der Schlüssel erscheint genau einmal, gespeichert wird nur sein Hash. Aufrufe im KI-Protokoll („ki:<Name>“).
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
const BASIS = `${String(import.meta.env.VITE_SUPABASE_URL ?? "https://qyzytbsptcmyjttyqkgf.supabase.co").replace(/\/+$/, "")}/functions/v1/ki-api`;
const RECHTE: { key: string; label: string; hinweis: string }[] = [
  { key: "kennzahlen", label: "Kennzahlen", hinweis: "Zusammengefasste Zahlen ohne Namen und Inhalte: Anmeldungen, Testphasen, Tarife, Kündigungsgründe, offene Tickets." },
  { key: "entwuerfe", label: "Entwürfe", hinweis: "Ticket-Antworten nur als Entwurf und interne Notizen. Du gibst in der Konsole frei." },
  { key: "betrieb_lesen", label: "Betreiber lesen", hinweis: "Kundenkonten, Tickets und Fehlermeldungen mit Personendaten. Höchstens 30 Tage gültig." },
];
const FUER: Record<string, string> = { claude: "Claude", chatgpt: "ChatGPT", sonstige: "Andere" };

type Zugang = { id: string; name: string; client: string; prefix: string; scopes: string[]; created_at: string; expires_at: string; revoked_at: string | null; last_used_at: string | null; use_count: number };

function Kopieren({ wert }: { wert: string }) {
  const [ok, setOk] = useState(false);
  return <button type="button" onClick={() => void navigator.clipboard.writeText(wert).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); })}>{ok ? "Kopiert ✓" : "Kopieren"}</button>;
}
function Zeile({ label, wert }: { label: string; wert: string }) {
  return <div className="stack" style={{ gap: 4 }}><strong>{label}</strong><div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}><code style={{ wordBreak: "break-all", flex: 1 }}>{wert}</code><Kopieren wert={wert} /></div></div>;
}

export function KiZugaenge() {
  const q = useQuery({ queryKey: ["platform_api_keys"], queryFn: async () => parse<Zugang[]>(await api.read("platform_api_keys", {})) });
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  const [name, setName] = useState("");
  const [fuer, setFuer] = useState("chatgpt");
  const [rechte, setRechte] = useState<string[]>(["kennzahlen"]);
  const [tage, setTage] = useState(90);
  const [neu, setNeu] = useState<{ key: string; client: string; expires_at: string } | null>(null);
  const voll = rechte.includes("betrieb_lesen");

  const anlegen = () => run("neu", async () => {
    const r = parse<{ key: string; expires_at: string }>(await api.rpc("platform_api_key_create", { p_name: name.trim(), p_client: fuer, p_scopes: rechte, p_days: voll ? Math.min(tage, 30) : tage }));
    setNeu({ key: r.key, client: fuer, expires_at: r.expires_at });
    setName("");
    await q.refetch();
  }, "Zugang angelegt.");
  const widerrufen = async (z: Zugang) => { if (await rueckfrage.ask({ title: "Zugang widerrufen?", text: `„${z.name}“ funktioniert danach sofort nicht mehr.`, confirmLabel: "Widerrufen", danger: true })) void run(z.id, async () => { await api.rpc("platform_api_key_revoke", { p_id: z.id }); await q.refetch(); }, "Widerrufen."); };

  return (
    <>
      <title>KI-Zugänge · Plattform · CreatorOffice</title>
      {rueckfrage.element}
      <h1>KI-Zugänge</h1>
      <p>Schlüssel für Claude, ChatGPT oder andere KI-Werkzeuge. Jeder Aufruf steht im <Link to="/betrieb/ki-protokoll">KI-Protokoll</Link>. Antworten an Kunden entstehen nur als Entwurf, freies SQL gibt es über Schlüssel nicht.</p>

      {neu && (
        <section className="panel stack" aria-labelledby="neu-h">
          <h2 id="neu-h">Dein neuer Zugang</h2>
          <div className="notice warning" role="alert">Der Schlüssel wird nur jetzt angezeigt. Kopiere ihn direkt in {FUER[neu.client]}. Gültig bis {date(neu.expires_at)}.</div>
          <Zeile label="Schlüssel" wert={neu.key} />
          {neu.client === "chatgpt" ? (<>
            <p><strong>ChatGPT, eigener GPT:</strong> GPT bearbeiten, dann Konfigurieren, Aktionen, Neue Aktion, „Import from URL“. Authentifizierung „API-Schlüssel“, Typ „Bearer“, Schlüssel von oben einfügen.</p>
            <Zeile label="Schema-URL" wert={`${BASIS}/openapi.json`} />
            <p><strong>Oder ChatGPT-Connector (MCP):</strong> Einstellungen, Apps und Connectoren, Entwicklermodus, Connector erstellen, Authentifizierung „Keine“, diese URL eintragen:</p>
            <Zeile label="Connector-URL" wert={`${BASIS}/mcp/${neu.key}`} />
          </>) : neu.client === "claude" ? (<>
            <p><strong>Claude (App oder claude.ai):</strong> Einstellungen, Connectoren, Eigenen Connector hinzufügen, diese URL eintragen:</p>
            <Zeile label="Connector-URL" wert={`${BASIS}/mcp/${neu.key}`} />
            <p><strong>Claude Code im Terminal:</strong></p>
            <Zeile label="Befehl" wert={`claude mcp add --transport http creatoroffice-ki ${BASIS}/mcp --header "Authorization: Bearer ${neu.key}"`} />
          </>) : (<>
            <Zeile label="REST (POST, Header Authorization: Bearer …)" wert={`${BASIS}/kennzahlen`} />
            <Zeile label="OpenAPI" wert={`${BASIS}/openapi.json`} />
            <Zeile label="MCP" wert={`${BASIS}/mcp/${neu.key}`} />
          </>)}
          <p><small>Die Connector-URL enthält den Schlüssel. Behandle sie wie ein Passwort.</small></p>
          <div><button type="button" onClick={() => setNeu(null)}>Fertig, Schlüssel ausblenden</button></div>
        </section>
      )}

      <section className="panel stack" aria-labelledby="anlegen-h">
        <h2 id="anlegen-h">Neuer Zugang</h2>
        <label className="stack" style={{ gap: 4 }}>Name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="z. B. ChatGPT SEO" /></label>
        <label className="stack" style={{ gap: 4 }}>Für
          <select value={fuer} onChange={(e) => setFuer(e.target.value)}>{Object.entries(FUER).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </label>
        <fieldset className="stack" style={{ gap: 8 }}>
          <legend>Rechte</legend>
          {RECHTE.map((r) => (
            <label key={r.key} className="row" style={{ gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" checked={rechte.includes(r.key)} onChange={(e) => setRechte((x) => e.target.checked ? [...x, r.key] : x.filter((y) => y !== r.key))} />
              <span><strong>{r.label}</strong><br /><small>{r.hinweis}</small></span>
            </label>
          ))}
        </fieldset>
        {voll && <div className="notice warning">„Betreiber lesen“ gibt der KI Kundendaten. Nutze es nur kurz und für eine konkrete Aufgabe.</div>}
        <label className="stack" style={{ gap: 4 }}>Gültig
          <select value={voll ? Math.min(tage, 30) : tage} onChange={(e) => setTage(Number(e.target.value))}>
            {[7, 30, 90, 365].filter((d) => !voll || d <= 30).map((d) => <option key={d} value={d}>{d} Tage</option>)}
          </select>
        </label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div><button type="button" className="primary" disabled={!!busy || !name.trim() || rechte.length === 0} aria-busy={busy === "neu"} onClick={() => void anlegen()}>{busy === "neu" ? "Wird angelegt …" : "Zugang anlegen"}</button></div>
      </section>

      <h2>Vorhandene Zugänge</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : (q.data ?? []).length === 0 ? (
        <div className="panel tk-empty"><p>Noch keine Zugänge.</p></div>
      ) : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">KI-Zugänge</caption>
            <thead><tr><th scope="col">Name</th><th scope="col">Für</th><th scope="col">Rechte</th><th scope="col">Gültig bis</th><th scope="col">Zuletzt genutzt</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {q.data!.map((z) => {
                const aktiv = !z.revoked_at && new Date(z.expires_at) > new Date();
                return (
                  <tr key={z.id}>
                    <td>{z.name}<br /><small><code>{z.prefix}…</code></small></td>
                    <td>{FUER[z.client] ?? z.client}</td>
                    <td>{z.scopes.map((s) => RECHTE.find((r) => r.key === s)?.label ?? s).join(", ")}</td>
                    <td>{date(z.expires_at)}</td>
                    <td>{z.last_used_at ? `${date(z.last_used_at)} ${time(z.last_used_at)} · ${z.use_count}×` : "nie"}</td>
                    <td>{aktiv ? <button type="button" disabled={!!busy} onClick={() => void widerrufen(z)}>Widerrufen</button> : <span className="pill">{z.revoked_at ? "widerrufen" : "abgelaufen"}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
